import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DataScienceIcon,
  DossiersTechniquesIcon,
  InfographiesIcon,
  FormationsIcon,
  AuditExpertiseIcon,
  InterventionsIcon,
} from './ServiceIcons';

interface ServiceCardProps {
  title: string;
  description: string;
  features: string[];
  iconType: 'data-science' | 'dossiers' | 'infographies' | 'formations' | 'audit' | 'interventions';
  color: string;
  slug: string;
  badge?: string;
}

export function ServiceCard({
  title,
  description,
  features,
  iconType,
  color,
  slug,
  badge,
}: ServiceCardProps) {
  const { t, i18n } = useTranslation('prestations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  // Handle absolute paths (starting with /) or relative paths
  const linkPath = slug.startsWith('/') ? `/${currentLocale}${slug}` : `/${currentLocale}/prestations/${slug}`;

  // Select appropriate icon component
  const IconComponent = {
    'data-science': DataScienceIcon,
    'dossiers': DossiersTechniquesIcon,
    'infographies': InfographiesIcon,
    'formations': FormationsIcon,
    'audit': AuditExpertiseIcon,
    'interventions': InterventionsIcon,
  }[iconType];

  return (
    <Link to={linkPath} className="group block h-full">
      <div className="relative h-full bg-white rounded-2xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-gray-200">
        {/* Badge optionnel */}
        {badge && (
          <div className="absolute top-4 right-4 z-10">
            <span className="px-3 py-1 bg-aspexit-green text-white text-xs font-semibold rounded-full">
              {badge}
            </span>
          </div>
        )}

        <div className="relative h-full flex flex-col p-8 bg-white">
          {/* Icon with circular colored background */}
          <div className="flex items-center justify-center mb-6">
            <div
              className="w-32 h-32 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${color}15, ${color}30)`,
              }}
            >
              <IconComponent className="w-20 h-20" color={color} />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-heading font-bold text-aspexit-black mb-3 leading-tight min-h-[4rem] flex items-center">
            {title}
          </h3>

          {/* Description */}
          <p className="text-aspexit-gray text-sm leading-relaxed mb-6 min-h-[5rem]">
            {description}
          </p>

          {/* Features list */}
          <ul className="space-y-3 mb-6 flex-1">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color }} />
                <span className="text-sm text-aspexit-gray">{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA with arrow */}
          <div className="flex items-center gap-2 font-semibold text-sm transition-all duration-300 group-hover:gap-3" style={{ color }}>
            <span>{t('serviceCard.learnMore')}</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  );
}
